import { useEffect, type RefObject } from "react";

/**
 * Grab-anywhere drag-to-pan for a scroll container: while the left button is
 * down and the pointer has moved past a small threshold, pointer movement
 * scrolls the element on both axes (an `is-panning` class is applied for
 * cursor/user-select styling). Pans never start on interactive children
 * (button/input/a) — those own their own drags.
 *
 * The click that ends a pan is swallowed here, in a capture-phase listener,
 * so callers' click handlers only ever see real clicks. Pointer capture can
 * retarget that click to the container itself (bypassing any row handler),
 * which is why the suppression lives on the container and the flag is also
 * cleared at the start of each new gesture in case no click arrives at all.
 */
export function usePanScroll(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let pan: { x: number; y: number; sl: number; st: number; moved: boolean } | null = null;
    let justPanned = false;

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      justPanned = false;
      if ((e.target as HTMLElement).closest("button, input, a")) return;
      pan = { x: e.clientX, y: e.clientY, sl: el.scrollLeft, st: el.scrollTop, moved: false };
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!pan) return;
      const dx = e.clientX - pan.x;
      const dy = e.clientY - pan.y;
      if (!pan.moved && Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
      if (!pan.moved) {
        pan.moved = true;
        el.setPointerCapture(e.pointerId);
        el.classList.add("is-panning");
      }
      el.scrollLeft = pan.sl - dx;
      el.scrollTop = pan.st - dy;
    };
    const onPointerEnd = () => {
      if (pan?.moved) {
        justPanned = true;
        el.classList.remove("is-panning");
      }
      pan = null;
    };
    const onClickCapture = (e: MouseEvent) => {
      if (!justPanned) return;
      justPanned = false;
      e.stopPropagation();
      e.preventDefault();
    };

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerEnd);
    el.addEventListener("pointercancel", onPointerEnd);
    el.addEventListener("click", onClickCapture, true);
    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerEnd);
      el.removeEventListener("pointercancel", onPointerEnd);
      el.removeEventListener("click", onClickCapture, true);
    };
  }, [ref]);
}
